import { useState, useContext } from "react";
import { Mail, Lock, Eye, EyeOff, LoaderCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import api from "../api/axios";

export default function LoginPage() {
  const { login } = useContext(AuthContext);
  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError("");
    setIsSubmitting(true);

    try {
      const res = await api.post("/auth/login", {
        emailOrPhone,
        password,
      });

      const data = res.data;

      const userData = {
        _id: data._id,
        fullName: data.fullName,
        email: data.email,
        phone: data.phone,
        role: data.role,
      };

      login(userData, data.token);

      if (data.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Server error. Try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-72px)] flex items-center justify-center bg-slate-950 px-4 py-10">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(37,99,235,0.35),_transparent_35%),radial-gradient(circle_at_bottom_right,_rgba(250,204,21,0.18),_transparent_30%)]" />
      <div className="relative w-full max-w-md rounded-[2rem] border border-white/15 bg-slate-900/75 p-8 text-white shadow-2xl backdrop-blur-xl">
        <p className="mx-auto mb-4 inline-flex rounded-full border border-cyan-300/35 bg-cyan-300/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-200">
          Secure sign in
        </p>
        <h2 className="mb-3 text-center text-3xl font-bold text-white">Sign in to MekPay</h2>
        <p className="mb-6 text-center text-sm leading-6 text-slate-300">
          Continue to your wallet, transfers, and transaction dashboard.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-200">Email or phone number</label>
            <div className="flex items-center rounded-xl border border-white/15 bg-white/5 px-3 py-2 focus-within:ring-2 focus-within:ring-cyan-300/35">
              <Mail className="mr-2 text-slate-300" size={20} />
              <input
                type="text"
                placeholder="name@example.com or +2348012345678"
                className="w-full bg-transparent text-white placeholder:text-slate-400 outline-none"
                value={emailOrPhone}
                onChange={(e) => setEmailOrPhone(e.target.value)}
                required
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-200">Password</label>
            <div className="flex items-center rounded-xl border border-white/15 bg-white/5 px-3 py-2 focus-within:ring-2 focus-within:ring-cyan-300/35">
              <Lock className="mr-2 text-slate-300" size={20} />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="********"
                className="w-full bg-transparent text-white placeholder:text-slate-400 outline-none"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="ml-2 text-slate-300 hover:text-white"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="text-right">
            <Link to="/forgot-password" className="text-cyan-300 text-sm hover:underline">
              Forgot Password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-accent py-3 font-semibold text-slate-950 transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSubmitting && <LoaderCircle size={18} className="animate-spin" aria-hidden="true" />}
            {isSubmitting ? "Signing in…" : "Sign in securely"}
          </button>

          {error && <p className="mt-3 rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">{error}</p>}
        </form>

        <p className="mt-6 text-center text-sm text-slate-300">
          Do not have an account?{" "}
          <Link to="/register" className="font-semibold text-cyan-300">
            Create one now
          </Link>
        </p>
      </div>
    </div>
  );
}
